<script setup lang="ts">
import { ref, watch, Ref, onMounted } from "vue";
import { debounce } from "vue-debounce";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { useApiClient } from "@/api/client";
import { useCampaignPlannerStore } from "../../store";
import OpportunityDO = Modules.CampaignPlanner.Data.OpportunityDO;
import { useRouter } from "vue-router";

const store = useCampaignPlannerStore();

onMounted(() =>
	store.setBreadcrumbs([
		{ title: "Opportunities", disabled: true, to: "" },
	])
);

const router = useRouter();

type BoolFilter = null | boolean;

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: { key: string; order: "asc" | "desc" }[];
}

const headers = [
	{ title: "Name", key: "name", align: "start", sortable: true },
	{ title: "Account", key: "account_name", align: "start", sortable: true },
	{
		title: "Sales Source",
		key: "sales_source",
		align: "start",
		sortable: true,
	},
	{ title: "Close Date", key: "close_date", align: "start", sortable: true },
	{
		title: "Synced",
		key: "synchronized_to_broadsign_at",
		align: "center",
		sortable: true,
	},
	{
		title: "Actions",
		key: "actions",
		align: "end",
		sortable: false,
		width: 100,
	},
] as const;

const opportunities: Ref<OpportunityDO[]> = ref([]);
const loading: Ref<boolean | string> = ref(false);
const total = ref(0);
const search = ref("");
const filterText = ref("");
const tableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: 25,
	sortBy: [],
});

const filters = ref<{
	sales_source: string | null;
	synchronized: BoolFilter;
}>({
	sales_source: null,
	synchronized: null,
});

watch(filterText, () => (search.value = String(Date.now())));
watch(filters, () => (search.value = String(Date.now())), { deep: true });

const buildFilterParams = (): Record<string, string> => {
	const params: Record<string, string> = {};

	if (filterText.value) params["filter[query]"] = filterText.value;
	if (filters.value.sales_source)
		params["filter[sales_source]"] = filters.value.sales_source;
	if (filters.value.synchronized !== null) {
		params["filter[synchronized]"] = filters.value.synchronized
			? "true"
			: "false";
	}

	return params;
};

const loadOpportunities = debounce(async (options: TableOptions) => {
	tableOptions.value = options;
	loading.value = "secondary";

	const { page, itemsPerPage, sortBy } = options;
	const sort = sortBy[0]
		? (sortBy[0].order === "desc" ? "-" : "") + sortBy[0].key
		: undefined;

	const response = await useApiClient("campaignPlanner").getOpportunities({
		page,
		per_page: itemsPerPage === -1 ? undefined : itemsPerPage,
		sort,
		...buildFilterParams(),
	});

	if (response.isOk()) {
		opportunities.value = response.value.data;
		total.value = response.value.total;
	}

	loading.value = false;
}, 300);

const formatDate = (value: string | null) => {
	if (!value) return "-";
	return dayjs(value).locale("nl").format("DD MMM YYYY");
};

const openOpportunity = (opportunity: OpportunityDO) => {
	router.push({
		name: "opportunity.detail",
		params: {
			opportunity_uuid: opportunity.uuid,
		},
	});
};
</script>

<template>
	<v-card elevation="1" class="mt-5">
		<div class="card-header">
			<v-text-field
				v-model="filterText"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				placeholder="Search by name, account or advertiser…"
				clearable
				hide-details
				variant="filled"
				density="compact"
			/>

			<div class="filter-strip">
				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Sales Source</span>
					<v-btn-toggle
						v-model="filters.sales_source"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn value="Inbound" size="small">Inbound</v-btn>
						<v-btn value="Outbound" size="small">Outbound</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Synced</span>
					<v-btn-toggle
						v-model="filters.synchronized"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn :value="true" size="small">Synced</v-btn>
						<v-btn :value="false" size="small">Not synced</v-btn>
					</v-btn-toggle>
				</div>
			</div>
		</div>

		<v-divider />

		<v-data-table-server
			:headers="headers"
			:items="opportunities"
			:items-per-page="tableOptions.itemsPerPage"
			:page="tableOptions.page"
			:items-length="total"
			:loading="loading"
			:search="search"
			:multi-sort="false"
			class="elevation-0"
			@update:options="loadOpportunities"
		>
			<template v-slot:item.name="{ item }">
				<v-tooltip
					:text="item.name"
					location="top"
					:disabled="item.name.length <= 30"
				>
					<template v-slot:activator="{ props }">
						<span class="truncate-cell" v-bind="props">{{ item.name }}</span>
					</template>
				</v-tooltip>
			</template>

			<template v-slot:item.account_name="{ item }">
				<v-tooltip
					:text="item.account_name"
					location="top"
					:disabled="item.account_name.length <= 20"
				>
					<template v-slot:activator="{ props }">
						<span class="truncate-cell" v-bind="props">{{
							item.account_name
						}}</span>
					</template>
				</v-tooltip>
			</template>

			<template v-slot:item.sales_source="{ item }">
				<v-chip size="small" variant="tonal">{{ item.sales_source }}</v-chip>
			</template>

			<template v-slot:item.close_date="{ item }">
				{{ formatDate(item.close_date ?? null) }}
			</template>

			<template v-slot:item.synchronized_to_broadsign_at="{ item }">
				<v-tooltip
					v-if="item.synchronized_to_broadsign_at"
					:text="formatDate(item.synchronized_to_broadsign_at)"
					location="top"
				>
					<template v-slot:activator="{ props }">
						<v-icon color="success" size="small" v-bind="props">
							fa: fa-solid fa-circle-check
						</v-icon>
					</template>
				</v-tooltip>
				<v-icon v-else color="medium-emphasis" size="small">
					fa: fa-regular fa-circle-xmark
				</v-icon>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="x-small"
					icon="fa: fa-solid fa-angle-right"
					color="primary"
					variant="text"
					@click="openOpportunity(item)"
				/>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss">
.card-header {
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.filter-strip {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 20px;
	align-items: flex-end;
}

.filter-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.truncate-cell {
	display: inline-block;
	max-width: 24ch;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	vertical-align: bottom;
}
</style>
